import { useMemo, useState } from "react"

import { formatProductChipLabel } from "../utils"

import { type AerosTagData } from "@/components/UiKit"
import type { SelectedProduct } from "@/screens/CustomerOrders/types"
import type { CompanyProductAssociationListItem } from "@/screens/CustomerProducts/stores/useCustomerProductAssociationsStore"

interface UseProductSelectionParams {
  products: CompanyProductAssociationListItem[]
}

interface UseProductSelectionReturn {
  selectedProducts: SelectedProduct[]
  rowSelection: Record<string, boolean>
  chips: AerosTagData[]
  handleSelectionChange: (next: Record<string, boolean>) => void
  handleChipRemove: (tag: AerosTagData) => void
  clearSelection: () => void
}

export const useProductSelection = ({ products }: UseProductSelectionParams): UseProductSelectionReturn => {
  const [selectedProductsMap, setSelectedProductsMap] = useState<Record<string, SelectedProduct>>({})

  const handleSelectionChange = (next: Record<string, boolean>) => {
    setSelectedProductsMap((prev) => {
      const updated: Record<string, SelectedProduct> = {}

      Object.keys(next).forEach((id) => {
        if (!next[id]) return

        const matchedItem = products.find((product) => String(product.productId) === id)
        const matchedProduct = matchedItem?.product
        const noteCount = [
          matchedItem?.bolNotes,
          matchedItem?.preTestNotes,
          matchedItem?.postTestNotes,
          matchedItem?.warehouseInstructions,
        ].filter((note) => Boolean(note?.trim())).length

        updated[id] = prev[id] ?? {
          productId: Number(id),
          label: formatProductChipLabel(matchedProduct),
          publicId: matchedProduct?.publicId ?? null,
          externalSystemId: matchedProduct?.externalSystemId ?? null,
          customerCode: matchedItem?.customerCode?.join(", ") ?? null,
          customerDescription: matchedItem?.customerDescription ?? null,
          companyProductDescription: matchedProduct?.companyProductDescription ?? null,
          supplierName: matchedProduct?.vendorDescription ?? null,
          computedProductStatus: matchedProduct?.computedProductStatus ?? null,
          packageUsWeightUom: matchedProduct?.packageUsWeightUom ?? null,
          packageMetricWeightUom: matchedProduct?.packageMetricWeightUom ?? null,
          packageUsWeight: matchedProduct?.packageUsWeight ?? null,
          packageMetricWeight: matchedProduct?.packageMetricWeight ?? null,
          packagesPerPallet: matchedProduct?.packagesPerPallet ?? null,
          noteCount,
        }
      })

      return updated
    })
  }

  const handleChipRemove = (tag: AerosTagData) => {
    setSelectedProductsMap((prev) => Object.fromEntries(Object.entries(prev).filter(([id]) => id !== String(tag.key))))
  }

  const clearSelection = () => setSelectedProductsMap({})

  const rowSelection = useMemo(
    () => Object.fromEntries(Object.keys(selectedProductsMap).map((id) => [id, true])),
    [selectedProductsMap]
  )

  const chips = useMemo<AerosTagData[]>(
    () => Object.values(selectedProductsMap).map((selected) => ({ key: selected.productId, label: selected.label })),
    [selectedProductsMap]
  )

  return {
    selectedProducts: Object.values(selectedProductsMap),
    rowSelection,
    chips,
    handleSelectionChange,
    handleChipRemove,
    clearSelection,
  }
}
